import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { ESSAYS } from "../data/study";
import { GLOSSARY_GROUPS, GLOSSARY_COUNT } from "../data/glossary";

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");

export default function Study() {
  return (
    <div data-testid="study-page">
      <PageHeader
        testId="study-header"
        eyebrow="The Study Hall · Beit Midrash"
        lines={["Essays, and", "the words to", "read them with."]}
        lede="Longer reads on the ideas beneath the Seven Laws, and a thorough glossary of the Hebrew terms you will meet along the way."
      />

      <div className="mx-auto max-w-7xl space-y-32 px-5 pb-28 sm:px-8">
        {/* Essays */}
        <section data-testid="study-essays" className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p id="essays" className="eyebrow scroll-mt-32">
              Essays
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
              The ideas beneath <span className="italic text-ink-soft">the laws.</span>
            </h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-ink-soft">
              Why seven, the image of G-d, providence and free will, repentance, and what is promised to the
              righteous of every nation.
            </p>
          </Reveal>
          <div className="border-t hairline lg:col-span-7 lg:col-start-6">
            {ESSAYS.map((e, i) => (
              <Reveal key={e.slug} y={16}>
                <Link
                  to={`/study/essays/${e.slug}`}
                  data-testid={`study-essay-${e.slug}`}
                  className="group grid grid-cols-[3rem_1fr_auto] items-center gap-5 border-b hairline py-7 sm:grid-cols-[4rem_1fr_auto] sm:gap-8"
                >
                  <span className="font-serif text-3xl leading-none text-terracotta">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block font-serif text-2xl leading-tight text-ink transition-transform duration-500 group-hover:translate-x-1 sm:text-3xl">
                      {e.title}
                    </span>
                    <span className="mt-1 block text-sm italic text-ink-muted">
                      {e.subtitle} · {e.readingTime}
                    </span>
                  </span>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border hairline transition-all duration-500 group-hover:border-terracotta group-hover:bg-terracotta group-hover:text-parchment">
                    <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:rotate-45" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Glossary */}
        <section data-testid="study-glossary">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <p id="glossary" className="eyebrow scroll-mt-32">
                Glossary · {GLOSSARY_COUNT} terms
              </p>
              <h2 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
                Some new words <span className="italic text-ink-soft">to meet.</span>
              </h2>
              <p className="mt-5 max-w-sm text-base leading-relaxed text-ink-soft">
                Each term with its Hebrew, a rough pronunciation, and a full explanation — enough to read the
                sources and talk with a teacher without feeling lost.
              </p>
              <nav aria-label="Glossary sections" className="mt-8 hidden lg:block">
                <ul className="space-y-2 border-l border-ink/10">
                  {GLOSSARY_GROUPS.map((g) => (
                    <li key={g.id}>
                      <a
                        href={`#glossary-${g.id}`}
                        data-testid={`glossary-nav-${g.id}`}
                        className="link-underline block pl-4 text-sm text-ink-soft hover:text-ink"
                      >
                        {g.title}
                        <span className="ml-2 font-mono text-[10px] text-ink-muted">{g.terms.length}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </Reveal>

            <div className="space-y-20 lg:col-span-7 lg:col-start-6">
              {GLOSSARY_GROUPS.map((g, gi) => (
                <div key={g.id} data-testid={`glossary-group-${g.id}`}>
                  <Reveal className="mb-6 flex items-baseline gap-4">
                    <span className="font-mono text-[10px] tracking-[0.22em] text-ink-muted">
                      {String(gi + 1).padStart(2, "0")}
                    </span>
                    <h3 id={`glossary-${g.id}`} className="scroll-mt-32 font-serif text-2xl italic text-ink sm:text-3xl">
                      {g.title}
                    </h3>
                    <span className="h-px flex-1 bg-ink/10" />
                  </Reveal>
                  <dl className="border-t hairline">
                    {g.terms.map((t) => (
                      <Reveal key={t.term} y={12}>
                        <div
                          data-testid={`glossary-${slugify(t.term)}`}
                          className="grid grid-cols-1 gap-3 border-b hairline py-6 sm:grid-cols-[14rem_1fr] sm:gap-8"
                        >
                          <dt>
                            <span className="block font-serif text-xl leading-snug text-ink">{t.term}</span>
                            {t.hebrew && (
                              <span dir="rtl" className="mt-1 block text-left font-serif text-xl text-terracotta">
                                {t.hebrew}
                              </span>
                            )}
                            {t.pron && (
                              <span className="mt-1 block font-mono text-[10px] tracking-[0.12em] text-ink-muted">{t.pron}</span>
                            )}
                          </dt>
                          <dd className="text-sm leading-[1.8] text-ink-soft sm:text-base">{t.meaning}</dd>
                        </div>
                      </Reveal>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
